import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiKey, ApiKeyCreated } from '@tabservice/tabfile-client';
import { KeyRound, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, SectionHeader } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm';
import { Field, Input } from '@/components/ui/field';
import { SecretOnce } from '@/components/ui/secret';
import { EmptyState, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { SortableTH, Table, TableWrap, TBody, TD, TH, THead, TR, useServerSort, TablePagination} from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { keysPageQuery, queryKeys } from '@/lib/queries';
import { tabfile, errorMessage } from '@/lib/api';
import { formatDate, formatRelative } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

export function KeysTab() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  // The column headers order the whole list, not the page on screen.
  const { sort, onSort, param: sortParam } = useServerSort(null, () => setPage(1));
  const keys = useQuery(keysPageQuery(page, PAGE_SIZE, sortParam));
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const createKey = useMutation({
    mutationFn: (keyName: string) => tabfile.POST('/v1/keys', { body: { name: keyName } }),
    onSuccess: (result) => {
      setCreated(result);
      setName('');
      void queryClient.invalidateQueries({ queryKey: queryKeys.keys });
    },
    onError: (error) => toast.error('Could not create the key', errorMessage(error)),
  });

  const revokeKey = useMutation({
    mutationFn: (keyId: string) => tabfile.DELETE('/v1/keys/{keyId}', { params: { path: { keyId } } }),
    onSuccess: () => {
      toast.success('Key revoked', 'Any system still using it will receive 401 responses.');
      setRevoking(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.keys });
    },
    onError: (error) => toast.error('Could not revoke the key', errorMessage(error)),
  });

  const items = keys.data?.items ?? [];


  return (
    <Card data-tour="keys">
      <SectionHeader
        title="API keys"
        description="API keys authenticate your systems. Treat them as confidential."
        actions={
          <Button
            onClick={() => {
              setCreated(null);
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New key
          </Button>
        }
      />

      {keys.isPending ? (
        <TableSkeleton rows={3} columns={5} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No keys yet"
          description="Create a key when a system needs to file on your behalf."
          action={
            <Button
              onClick={() => {
                setCreated(null);
                setCreateOpen(true);
              }}
            >
              New key
            </Button>
          }
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <SortableTH columnKey="name" sort={sort} onSort={onSort}>
                    Name
                  </SortableTH>
                  <TH>Prefix</TH>
                  <SortableTH columnKey="mode" sort={sort} onSort={onSort}>
                    Mode
                  </SortableTH>
                  <SortableTH columnKey="status" sort={sort} onSort={onSort}>
                    Status
                  </SortableTH>
                  <SortableTH columnKey="lastUsed" sort={sort} onSort={onSort}>
                    Last used
                  </SortableTH>
                  <SortableTH columnKey="created" sort={sort} onSort={onSort}>
                    Created
                  </SortableTH>
                  <TH aria-label="Actions" />
                </tr>
              </THead>
              <TBody>
                {items.map((item) => (
                  <TR key={item.id}>
                    <TD className="font-medium">{item.name}</TD>
                    <TD className="text-muted-foreground">{item.prefix}</TD>
                    <TD>
                      <Pill tone={item.mode === 'live' ? 'warning' : 'info'}>
                        {item.mode === 'live' ? 'Live' : 'IRS test'}
                      </Pill>
                    </TD>
                    <TD>
                      <Pill tone={item.status === 'active' ? 'success' : 'neutral'}>
                        {item.status === 'active' ? 'Active' : 'Revoked'}
                      </Pill>
                    </TD>
                    <TD className="text-muted-foreground">{item.lastUsedAt ? formatRelative(item.lastUsedAt) : 'never'}</TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{formatDate(item.createdAt)}</TD>
                    <TD className="text-right">
                      {item.status === 'active' ? (
                        <Button variant="destructive" size="sm" onClick={() => setRevoking(item)}>
                          Revoke
                        </Button>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={keys.data?.total ?? keys.data?.items?.length ?? 0}
            busy={keys.isFetching}
            onPageChange={setPage}
          />
        </>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          title={created ? 'New API key' : 'Create an API key'}
          description={
            created ? 'The key is shown once.' : 'Name it after the system that will use it.'
          }
          footer={
            created ? (
              <DialogClose asChild>
                <Button variant="primary">Done</Button>
              </DialogClose>
            ) : (
              <>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button
                  variant="primary"
                  disabled={name.trim() === '' || createKey.isPending}
                  onClick={() => createKey.mutate(name.trim())}
                >
                  {createKey.isPending ? <Spinner /> : null}
                  Create key
                </Button>
              </>
            )
          }
        >
          {created ? (
            <SecretOnce
              label="The key"
              value={created.key}
              note={`Prefix ${created.prefix}. Send it as the X-Api-Key header.`}
            />
          ) : (
            <Field label="Name" hint="For example: payroll ingest, or nightly batch.">
              {(props) => (
                <Input
                  {...props}
                  value={name}
                  autoFocus
                  maxLength={80}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Payroll ingest"
                />
              )}
            </Field>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null);
        }}
        title="Revoke this key"
        description={revoking ? `${revoking.name}, prefix ${revoking.prefix}` : undefined}
        body={
          <p className="text-base text-muted-foreground">
            Anything using this key stops working immediately. E-files already sent are not affected.
          </p>
        }
        confirmLabel="Revoke key"
        destructive
        pending={revokeKey.isPending}
        onConfirm={() => {
          if (revoking) revokeKey.mutate(revoking.id);
        }}
      />
    </Card>
  );
}
